import { useEffect, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { USAGE_LIMIT_BANNER } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { PRICING_PAGE_REDIRECT } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import { getProductNameTextFromApiName } from '../../_core/common/adapters/getProductNameText';
import { track } from '../../_core/common/eventTracking/tracker';
import getUpgradeBannerTranslations from '../../_core/utils/getUpgradeBannerTranslations';
import { getUpgradeDataFromUpgradeConfig } from '../../_core/utils/getUpgradeDataFromUpgradeConfig';
import { getUsersWaitingForInviteTranslations } from '../../_core/utils/getUsersWaitingForInviteTranslations';
import { getUpgradeConfigTrackingProperties, updateViewedUpgradePointsCache } from '../utils';
import DefaultBanner from './DefaultBanner';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const UsageLimitBanner = ({
  upgradePoint,
  limit,
  value,
  displayThreshold,
  hasReachedLimit,
  'data-test-id': dataTestId = 'usage-limit-banner',
  displayBannerOverride = false
}) => {
  const upgradeData = getUpgradeDataFromUpgradeConfig(upgradePoint);
  const usageLimitBannerData = useMemo(() => ({
    limit,
    value,
    displayThreshold,
    hasReachedLimit
  }), [limit, value, displayThreshold, hasReachedLimit]);
  useEffect(() => {
    updateViewedUpgradePointsCache(upgradePoint.upgradeKey, () => {
      track('interaction', Object.assign({
        action: 'viewed upgrade point'
      }, usageLimitBannerData, getUpgradeConfigTrackingProperties(upgradePoint)));
    });
  }, [upgradePoint, usageLimitBannerData]);
  if (!displayBannerOverride && displayThreshold && value < displayThreshold) return null;
  const {
    bannerTitle,
    bannerSubtitle
  } = getUpgradeBannerTranslations(upgradePoint.descriptions);
  let bannerTitleText = bannerTitle;
  let partialBannerSubtext = bannerSubtitle;
  if (typeof bannerTitleText === 'string') {
    // Format numbers with thousands separator. ex: 1000000 -> 1,000,000
    bannerTitleText = bannerTitleText.replace(/\b\d+\b/g, match => I18n.formatNumber(I18n.parseNumber(match)));
  }

  // Special handling to render custom copy for Freetos usage limit banner
  if (upgradePoint.upgradeKey === 'user-management-waiting-for-invite-limit-banner') {
    const customTranslations = getUsersWaitingForInviteTranslations(value);
    if (customTranslations.bannerTitleText) {
      bannerTitleText = customTranslations.bannerTitleText;
    }
    if (customTranslations.partialBannerSubtext) {
      partialBannerSubtext = customTranslations.partialBannerSubtext;
    }
  }
  const translatedProductName = getProductNameTextFromApiName(upgradeData.apiName);
  const bannerSubtext = `${partialBannerSubtext} ${translatedProductName}.`;
  return /*#__PURE__*/_jsx(DefaultBanner, {
    titleText: /*#__PURE__*/_jsx(_Fragment, {
      children: bannerTitleText
    }),
    dataTestId: dataTestId,
    subtext: /*#__PURE__*/_jsx(_Fragment, {
      children: bannerSubtext
    }),
    button: /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
      upgradeData: upgradeData,
      usageLimitBannerData: usageLimitBannerData,
      location: USAGE_LIMIT_BANNER,
      level: 1,
      use: "secondary",
      purchaseMotions: [PRICING_PAGE_REDIRECT],
      "data-test-id": "upgrade-button",
      isUpgradePoint: true,
      goToUpgradePage: true,
      truncate: false,
      size: "extra-small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.upgradeButton.default"
      })
    })
  });
};
export default UsageLimitBanner;